import { useEffect, useRef, useState } from 'react';
import { Bold, Italic, Underline, List, ListOrdered, History, Check } from 'lucide-react';
import { api } from '../api/client';
import { sanitizeNotesHtml } from '../lib/html';
import { useT } from '../i18n';

interface NoteVersion {
  id: number;
  content: string;
  created_at: string;
}

interface Props {
  sessionParticipantId: number;
  /** Stored notes (plain text from older entries or sanitized HTML). */
  value: string;
  /** Called with the sanitized HTML after each autosave debounce. */
  onSave: (html: string) => Promise<void> | void;
  placeholder: string;
}

// One save chain per participant, module-scoped: an unmount flush from a dying
// editor instance and the saves of a freshly remounted one (key= remount, or
// leaving and reopening the séance) must stay strictly ordered.
const noteSaveChains = new Map<number, Promise<void>>();

/** Older notes were stored as plain text — render their line breaks. */
function toEditorHtml(stored: string): string {
  if (/<\w+[^>]*>/.test(stored)) return sanitizeNotesHtml(stored);
  return stored
    .split('\n')
    .map((l) => `<div>${l.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;') || '<br>'}</div>`)
    .join('');
}

/**
 * Auto-expanding rich notes (PMO-style growth, plus a light formatting bar).
 * Autosaves while typing with a visible saved indicator; "Enregistrer une
 * version" snapshots the current text so the intervenant can revert.
 */
export function RichNotes({ sessionParticipantId, value, onSave, placeholder }: Props) {
  const { t, lang } = useT();
  const editorRef = useRef<HTMLDivElement>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [empty, setEmpty] = useState(true);
  const [versions, setVersions] = useState<NoteVersion[] | null>(null);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [versionSaved, setVersionSaved] = useState(false);
  const popRef = useRef<HTMLDivElement>(null);
  // Save lifecycle: the debounce timer must never read the DOM at fire time —
  // after unmount the ref is null and reading it saved '' over real notes. The
  // HTML is captured at schedule time instead, and a pending edit is flushed
  // (not dropped) when the editor unmounts.
  const latestHtmlRef = useRef('');
  const lastSavedRef = useRef<string | null>(null);
  const pendingRef = useRef(false);
  const onSaveRef = useRef(onSave);
  useEffect(() => { onSaveRef.current = onSave; });

  // Load stored content when switching participants.
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    el.innerHTML = toEditorHtml(value ?? '');
    latestHtmlRef.current = sanitizeNotesHtml(el.innerHTML);
    lastSavedRef.current = latestHtmlRef.current;
    pendingRef.current = false;
    setEmpty(el.innerText.trim().length === 0);
    setSaveState('idle');
    setSavedAt(null);
    setVersions(null);
    setVersionsOpen(false);
  }, [sessionParticipantId]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Unmount: cancel the timer and flush any content not yet confirmed saved —
  // including a save that already fired but FAILED (the chain's run() no-ops
  // when the content did land, so this is retry-or-no-op, never a double
  // write). The PATCH itself outlives the component.
  useEffect(() => () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    if (latestHtmlRef.current !== lastSavedRef.current) {
      void doSave(latestHtmlRef.current);
    }
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!versionsOpen) return;
    const onDown = (e: PointerEvent) => {
      if (popRef.current && !popRef.current.contains(e.target as Node)) setVersionsOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [versionsOpen]);

  // A visually empty editor normalizes to '' — contentEditable leaves residue
  // like <div><br></div> behind, which must never masquerade as content.
  const currentHtml = () => {
    const el = editorRef.current;
    if (!el || el.innerText.trim().length === 0) return '';
    return sanitizeNotesHtml(el.innerHTML);
  };

  // Saves are chained per participant so two in-flight PATCHes can never land
  // out of order (a slow older save must not overwrite a newer one).
  const doSave = (html: string) => {
    pendingRef.current = false;
    const run = async () => {
      if (html === lastSavedRef.current) {
        setSaveState((s) => (s === 'saving' ? 'idle' : s));
        return;
      }
      try {
        await onSaveRef.current(html);
        lastSavedRef.current = html;
        setSaveState('saved');
        setSavedAt(new Date());
      } catch {
        // Failed content stays "dirty": editing retries it, and the unmount
        // flush picks it up (it compares against lastSavedRef, not pending).
        pendingRef.current = true;
        setSaveState('error');
      }
    };
    const chained = (noteSaveChains.get(sessionParticipantId) ?? Promise.resolve()).then(run, run);
    noteSaveChains.set(sessionParticipantId, chained);
    return chained;
  };

  const scheduleSave = () => {
    const el = editorRef.current;
    if (!el) return;
    setEmpty(el.innerText.trim().length === 0);
    // Capture the content NOW, while the DOM node exists — the timer callback
    // must never read the editor ref (null after unmount).
    latestHtmlRef.current = currentHtml();
    if (latestHtmlRef.current === lastSavedRef.current && !pendingRef.current) return;
    pendingRef.current = true;
    setSaveState('saving');
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => { void doSave(latestHtmlRef.current); }, 600);
  };

  const exec = (command: string) => {
    editorRef.current?.focus();
    document.execCommand(command);
    scheduleSave();
  };

  const loadVersions = async () => {
    const rows = await api.get<NoteVersion[]>(`/api/session-participants/${sessionParticipantId}/note-versions`);
    setVersions(rows);
  };

  const saveVersion = async () => {
    if (!editorRef.current || editorRef.current.innerText.trim().length === 0) return;
    const html = currentHtml();
    // Flush the live autosave first so the snapshot matches what's stored.
    if (saveTimer.current) clearTimeout(saveTimer.current);
    latestHtmlRef.current = html;
    await doSave(html);
    await api.post(`/api/session-participants/${sessionParticipantId}/note-versions`, { content: html });
    setVersionSaved(true);
    setTimeout(() => setVersionSaved(false), 2000);
    if (versions !== null) await loadVersions();
  };

  const restoreVersion = (v: NoteVersion) => {
    const el = editorRef.current;
    if (!el) return;
    el.innerHTML = toEditorHtml(v.content);
    setVersionsOpen(false);
    scheduleSave();
  };

  const formatTime = (d: Date) =>
    d.toLocaleTimeString(lang === 'fr' ? 'fr-CH' : 'en-CH', { hour: '2-digit', minute: '2-digit' });
  const formatVersionDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString(lang === 'fr' ? 'fr-CH' : 'en-CH', {
      day: 'numeric', month: 'short',
    }) + ' · ' + formatTime(d);
  };

  return (
    <div className="richnotes">
      <div className="richnotes-bar" role="toolbar" aria-label={t('notes.toolbar')}>
        <button type="button" className="richnotes-tool" title={t('notes.bold')} onMouseDown={(e) => { e.preventDefault(); exec('bold'); }}>
          <Bold size={14} />
        </button>
        <button type="button" className="richnotes-tool" title={t('notes.italic')} onMouseDown={(e) => { e.preventDefault(); exec('italic'); }}>
          <Italic size={14} />
        </button>
        <button type="button" className="richnotes-tool" title={t('notes.underline')} onMouseDown={(e) => { e.preventDefault(); exec('underline'); }}>
          <Underline size={14} />
        </button>
        <span className="richnotes-bar-sep" />
        <button type="button" className="richnotes-tool" title={t('notes.ul')} onMouseDown={(e) => { e.preventDefault(); exec('insertUnorderedList'); }}>
          <List size={14} />
        </button>
        <button type="button" className="richnotes-tool" title={t('notes.ol')} onMouseDown={(e) => { e.preventDefault(); exec('insertOrderedList'); }}>
          <ListOrdered size={14} />
        </button>

        <span className="richnotes-bar-spacer" />

        <span
          className={`richnotes-savestate ${saveState === 'saving' ? 'richnotes-savestate--saving' : ''} ${saveState === 'error' ? 'richnotes-savestate--error' : ''}`}
          aria-live="polite"
        >
          {saveState === 'saving' && t('common.saving')}
          {saveState === 'saved' && savedAt && <><Check size={12} strokeWidth={2.5} /> {t('notes.savedAt')} {formatTime(savedAt)}</>}
          {saveState === 'error' && t('notes.saveError')}
        </span>

        <div className="richnotes-versions" ref={popRef}>
          <button
            type="button"
            className="richnotes-tool richnotes-tool--label"
            onClick={async () => {
              const opening = !versionsOpen;
              setVersionsOpen(opening);
              if (opening && versions === null) await loadVersions();
            }}
            title={t('notes.versionsHelp')}
          >
            <History size={13} /> {t('notes.versions')}
          </button>
          {versionsOpen && (
            <div className="richnotes-pop">
              <button type="button" className="richnotes-pop-save" onClick={saveVersion}>
                {versionSaved ? <><Check size={13} /> {t('notes.versionSaved')}</> : t('notes.saveVersion')}
              </button>
              <div className="richnotes-pop-list">
                {versions === null && <div className="richnotes-pop-empty">…</div>}
                {versions !== null && versions.length === 0 && (
                  <div className="richnotes-pop-empty">{t('notes.noVersions')}</div>
                )}
                {versions?.map((v) => (
                  <button key={v.id} type="button" className="richnotes-pop-item" onClick={() => restoreVersion(v)}>
                    <span className="richnotes-pop-date">{formatVersionDate(v.created_at)}</span>
                    <span className="richnotes-pop-preview">
                      {new DOMParser().parseFromString(v.content, 'text/html').body.textContent?.slice(0, 60)}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="richnotes-editorwrap">
        <div
          ref={editorRef}
          className="richnotes-editor"
          contentEditable
          role="textbox"
          aria-multiline="true"
          aria-label={t('editor.notes')}
          onInput={scheduleSave}
          onBlur={scheduleSave}
          suppressContentEditableWarning
        />
        {empty && <div className="richnotes-placeholder" aria-hidden>{placeholder}</div>}
      </div>
    </div>
  );
}
